import { Route } from '@/Enums/Routes/Route.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import LoadingBackdrop from '@/components/Grid/LoadingBackdrop';
import SearchInput from '@/components/SearchInput';
import IOSSwitch from '@/components/Switch';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectCampaignManagementIsArchivedFilter } from '@/store/selectors/campaignManagement.selectors';
import { selectIsAdmin, selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { setFilters } from '@/store/slices/campaignManagementSlice';
import { Add, FileDownloadOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  GridToolbarContainer,
  gridFilteredSortedRowIdsSelector,
  selectedGridRowsSelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ColumnManagement from './ColumnManagement';

const getSelectedRowsToExport = ({ apiRef }) => {
  const selectedRowIds = selectedGridRowsSelector(apiRef);
  if (selectedRowIds.size > 0) {
    const rowIds = Array.from(selectedRowIds.keys());
    const fullList = new Set();
    rowIds.forEach(rowId => {
      fullList.add(rowId);
      apiRef.current
        .getRowGroupChildren({
          groupId: rowId,
          applyFiltering: true,
          applySorting: true,
          skipAutoGeneratedRows: false,
        })
        ?.forEach(id => fullList.add(id));
    });

    return Array.from(fullList.values());
  }

  return gridFilteredSortedRowIdsSelector(apiRef);
};

const ToolBar = memo(
  ({
    counter,
    loading,
    editMode,
    toggleEditMode,
    columns,
    columnVisibilityModel,
    onColumnReorder,
    onColumnVisibilityChange,
    onColumnManagementReset,
    saveColumnConfig,
  }) => {
    const apiRef = useGridApiContext();
    const dispatch = useAppDispatch();
    const search = useAppSelector(({ campaignManagement }) => campaignManagement.filters?.search);
    const [searchValue, setSearchValue] = useState(search);

    const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.CAMPAIGN_MANAGEMENT));
    const isAdmin = useAppSelector(selectIsAdmin);

    const isArchived = useAppSelector(selectCampaignManagementIsArchivedFilter);

    const onActiveChange = useCallback(() => {
      dispatch(setFilters({ isArchived: !isArchived }));
    }, [dispatch, isArchived]);

    const handleSearch = useCallback(
      debounce(newSearch => {
        dispatch(setFilters({ search: newSearch }));
      }, 500),
      [dispatch],
    );

    // Keep the box in step when search changes from outside it (a saved view, Clear all),
    // and drop any pending keystrokes so they don't overwrite it.
    useEffect(() => {
      handleSearch.clear();
      setSearchValue(search);
    }, [search, handleSearch]);

    const handleSearchValueChange = useCallback(
      ({ target: { value } }) => {
        setSearchValue(value);
        handleSearch(value);
      },
      [handleSearch],
    );

    const onGenerateReportClick = () => {
      apiRef.current.exportDataAsCsv({
        getRowsToExport: getSelectedRowsToExport,
        fileName: `Campaign_Management_${Date.now()}`,
      });
    };

    const autosizeGrid = useCallback(() => {
      apiRef?.current?.autosizeColumns?.({
        ...EXPAND_OPTIONS,
        columns: [...columns.map(({ field }) => field), GRID_TREE_DATA_GROUPING_FIELD],
      });
    }, [apiRef]);

    return (
      <>
        <GridToolbarContainer sx={{ padding: 0 }}>
          <div className='py-[24px] px-[30px] flex items-center justify-between relative w-full border-b'>
            <span className='font-semibold text-[20px]'>
              {numberToUsFormat(counter)} result{counter !== 1 ? 's' : ''} found
            </span>
            <div className='flex gap-[10px]'>
              <SearchInput placeholder='Search by name' value={searchValue} onChange={handleSearchValueChange} />
              <Button variant='outlined' onClick={toggleEditMode} disabled={loading || readOnly}>
                <span>Edit Mode</span>
                <IOSSwitch sx={{ marginLeft: '8px' }} checked={editMode} />
              </Button>
              {isAdmin && (
                <Button variant='outlined' onClick={onActiveChange} sx={{ height: '37px' }} disabled={loading}>
                  <span>Archived</span>
                  <IOSSwitch sx={{ marginLeft: '7px' }} checked={isArchived} />
                </Button>
              )}
              <Button variant='outlined' onClick={onGenerateReportClick}>
                <FileDownloadOutlined sx={{ width: 20, height: 20, marginRight: '4px' }} />
                <span>Export</span>
              </Button>
              <Button variant='contained' component={Link} to={`${Route.CAMPAIGN_MANAGEMENT}/new`} disabled={readOnly}>
                <Add sx={{ width: 20, height: 20, marginRight: '4px' }} />
                <span>Create New Campaign</span>
              </Button>
              <ColumnManagement
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                onColumnReorder={onColumnReorder}
                onColumnVisibilityChange={onColumnVisibilityChange}
                onReset={onColumnManagementReset}
                autosizeGrid={autosizeGrid}
                saveColumnConfig={saveColumnConfig}
              />
            </div>
            {loading ? <LoadingBackdrop /> : null}
          </div>
        </GridToolbarContainer>
      </>
    );
  },
);

ToolBar.propTypes = {
  counter: PropTypes.number,
  loading: PropTypes.bool,
  editMode: PropTypes.bool,
  toggleEditMode: PropTypes.func,
  columns: PropTypes.array,
  columnVisibilityModel: PropTypes.object,
  onColumnReorder: PropTypes.func,
  onColumnVisibilityChange: PropTypes.func,
  onColumnManagementReset: PropTypes.func,
  saveColumnConfig: PropTypes.func,
};

export default ToolBar;
